import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Tracked from '~/components/Tracked';
import type { TrackedButtonClickName } from '~/services/gtm.client';

type Props = {
  isExpanded: boolean;
  onSizeSelect: (size: string) => void;
  selectedSize?: string;
  sizes: string[];
};

export default function SizeSelectorGrid({
  onSizeSelect,
  selectedSize,
  sizes,
  isExpanded
}: Props) {
  const { t } = useTranslation('pdp');

  return (
    <div
      className={`no-scrollbar w-full min-w-0 gap-2 px-4 ${isExpanded ? 'grid grid-cols-[repeat(auto-fill,minmax(60px,1fr))]' : 'flex flex-row flex-nowrap overflow-x-auto'}`}
      style={{
        maskImage:
          'linear-gradient(to right, transparent 0%, black 16px, black calc(100% - 16px), transparent 100%)'
      }}
    >
      <SizeButton
        isGridExpanded={isExpanded}
        name="ALL_SIZES"
        isSelected={selectedSize === ''}
        onClick={() => onSizeSelect('')}
      >
        {t('all')}
      </SizeButton>
      {sizes.map((size) => (
        <SizeButton
          isGridExpanded={isExpanded}
          key={size}
          name={`SIZE_${size}`}
          isSelected={selectedSize === size}
          onClick={() => onSizeSelect(size)}
        >
          {size}
        </SizeButton>
      ))}
    </div>
  );
}

function SizeButton({
  children,
  isSelected,
  isGridExpanded,
  name,
  onClick
}: {
  children: string;
  isGridExpanded: boolean;
  isSelected: boolean;
  name: TrackedButtonClickName;
  onClick: () => void;
}) {
  const baseClassNames = 'w-full h-full text-sm rounded-full p-2';

  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (isSelected && !isGridExpanded) {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [isSelected, isGridExpanded]);

  return (
    <span
      className={
        isGridExpanded ? 'w-full' : 'min-w-[60px] max-w-[72px] grow basis-0'
      }
      ref={ref}
    >
      <Tracked.Button
        name={name}
        variant="none"
        pagePosition="Size Selection"
        className={`${baseClassNames} ${isSelected ? 'bg-black text-white' : 'border border-grey-divide text-black'}`}
        onClick={onClick}
      >
        {children}
      </Tracked.Button>
    </span>
  );
}
